import {useState} from 'react'
import {Strong} from '@/components/shared/Strong'
import {useNavigate} from 'react-router-dom'
import {Bell, CheckCheck} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {useI18n} from '@/i18n'
import {routes} from '@/lib/routes'
import {formatTimestamp} from '@/lib/date-utils'
import {useUnreadCount, useRecentNotifications, useMarkAllRead} from './useNotifications'
import type {Notification} from '@/types/notification'
import {Icon} from '@/components/shared/Icon'
import {InlineRow} from '@/components/shared/InlineRow'
import {CountBadge} from '@/components/shared/CountBadge'
import {MutedText} from '@/components/shared/MutedText'
import {SmallText} from '@/components/shared/SmallText'
import {CompactButton} from '@/components/shared/CompactButton'
import {Stack} from '@/components/shared/Stack'
import {ScrollableBox} from '@/components/shared/ScrollableBox'
import {ExpandableRow} from '@/components/shared/ExpandableRow'
import {PulseDot} from '@/components/shared/PulseDot'

export function NotificationBell() {
    const i18n = useI18n()
    const navigate = useNavigate()
    const [open, setOpen] = useState(false)

    const {data: unreadCount = 0} = useUnreadCount()
    const {data: recentData} = useRecentNotifications(open)
    const markAllRead = useMarkAllRead()

    const notifications = recentData?.content ?? []

    const handleNotificationClick = (notification: Notification) => {
        navigate(routes.my.notifications.detail(notification.id))
        setOpen(false)
    }

    const handleMarkAllRead = () => {
        markAllRead.mutate()
    }

    const handleViewAll = () => {
        setOpen(false)
        navigate(routes.my.notifications.path())
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="relative"
                    aria-label={i18n.notifications.bellTooltip as string}
                    data-testid="notification-bell"
                >
                    <Icon icon={Bell}/>
                    <CountBadge count={unreadCount} floating data-testid="unread-badge"/>
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
                <InlineRow surface="toolbar" justify="between">
                    <Strong>{i18n.notifications.title}</Strong>
                    {unreadCount > 0 && (
                        <CompactButton
                            size="xs"
                            variant="ghost"
                            onClick={handleMarkAllRead}
                            data-testid="mark-all-read"
                        >
                            <Icon icon={CheckCheck} size="xs" className="mr-1"/>
                            {i18n.notifications.markAllRead}
                        </CompactButton>
                    )}
                </InlineRow>
                <ScrollableBox flush autoHeight className="max-h-[300px] overflow-y-auto">
                    {notifications.length === 0 ? (
                        <MutedText size="sm" block centered className="px-4 py-6">
                            {i18n.notifications.noNotifications}
                        </MutedText>
                    ) : (
                        <Stack divideY>
                            {notifications.map((notification) => (
                                <ExpandableRow
                                    key={notification.id}
                                    onClick={() => handleNotificationClick(notification)}
                                    data-testid={`notification-item-${notification.id}`}
                                    className="px-4 py-3"
                                >
                                    <InlineRow align="start" gap="md">
                                        {!notification.read && (
                                            <PulseDot tone="primary" size="sm" staticDot className="mt-1.5 shrink-0"/>
                                        )}
                                        <Stack gap="xs" className={!notification.read ? undefined : 'ml-5'}>
                                            <SmallText className="font-medium leading-tight" data-testid={`notification-item-title-${notification.id}`}>
                                                {notification.title}
                                            </SmallText>
                                            <MutedText size="xs" className="line-clamp-2" block>
                                                {notification.message}
                                            </MutedText>
                                            <MutedText size="xs" block>
                                                {formatTimestamp(notification.createdAt)}
                                            </MutedText>
                                        </Stack>
                                    </InlineRow>
                                </ExpandableRow>
                            ))}
                        </Stack>
                    )}
                </ScrollableBox>
                <InlineRow surface="toolbarTop">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs"
                        onClick={handleViewAll}
                        data-testid="view-all-notifications"
                    >
                        {i18n.notifications.viewAll}
                    </Button>
                </InlineRow>
            </PopoverContent>
        </Popover>
    )
}
